// Hooks for the `nf-drawer` page transition (styles/_nf-arrival.scss), which uncovers a page from
// the top of the area it is seen in. Bind them on the <Transition>:
//
//   <Transition name="nf-drawer" mode="out-in" @enter="onDrawerEnter" @after-enter="onDrawerSettled"
//     @enter-cancelled="onDrawerSettled">
//
// Vue calls @enter right after inserting the element, in the same frame the enter classes start
// the animation, so what it sets is there for the animation's first frame.
//
// Keep the copies in NFriends.FileSystem.Repo.Workbench/src/services/pageDrawer.ts and
// NFriends.Schedule.AspNetCore.Dashboard.Frontend/src/services/pageDrawer.ts identical to this file.

const CLIPPED = "nfDrawerClipped";

export function onDrawerEnter(el: Element) {
  if (!(el instanceof HTMLElement)) return;

  // The mask is laid out on the page's own box. Where the page is seen from starts at the lower
  // edge of a sticky navbar (--nf-navbar-height) or at the top of the element that scrolls it,
  // whichever is lower; a page entered scrolled down is then uncovered from there, not from a
  // top edge above what is shown.
  let line = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--nf-navbar-height")) || 0;
  const scroller = scrollParent(el);
  if (scroller) line = Math.max(line, scroller.getBoundingClientRect().top);
  // The box as laid out: the spring's starting offset is already applied to it here.
  const shift = parseFloat(getComputedStyle(el).translate.split(" ")[1] ?? "0") || 0;
  el.style.setProperty("--nf-drawer-top", `${Math.max(0, line - (el.getBoundingClientRect().top - shift))}px`);

  // The spring runs the page a little below its place, which counts as scrollable overflow and
  // would flash a scrollbar on a page that fills the window exactly. A container that does not
  // scroll clips it for the moment it lasts (clip, not hidden: no scroll container, so a sticky
  // navbar stays stuck). A container that scrolls is left as it is - switching it would drop its
  // scroll position.
  const parent = el.parentElement;
  if (parent && getComputedStyle(parent).overflowY === "visible") {
    parent.style.overflowY = "clip";
    el.dataset[CLIPPED] = "";
  }
}

// Drops what onDrawerEnter set once the page is in (or its entrance was cut short by the next
// navigation), so nothing of the transition stays behind.
export function onDrawerSettled(el: Element) {
  if (!(el instanceof HTMLElement)) return;
  el.style.removeProperty("--nf-drawer-top");
  if (CLIPPED in el.dataset) {
    delete el.dataset[CLIPPED];
    el.parentElement?.style.removeProperty("overflow-y");
  }
}

function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    const overflowY = getComputedStyle(p).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return p;
  }
  return null;
}
